import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { KeyRound, ArrowLeft } from 'lucide-react';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import Alert from '../components/common/Alert';
import { authService } from '../services/authService';
import { validateEmail, validateRequired, validateCPF } from '../utils/validators';

const DependentSetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeAuth } = useAuth();

  const prefilledIdentifier = (location.state as { identifier?: string } | null)?.identifier || '';

  const [formData, setFormData] = useState({
    identifier: prefilledIdentifier,
    password: '',
    password_confirmation: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!validateRequired(formData.identifier)) {
      newErrors.identifier = 'Informe seu e-mail ou CPF';
    } else if (!validateEmail(formData.identifier) && !validateCPF(formData.identifier)) {
      newErrors.identifier = 'E-mail ou CPF inválido';
    }

    if (!formData.password || formData.password.length < 6) {
      newErrors.password = 'A senha deve ter ao menos 6 caracteres';
    }

    if (formData.password !== formData.password_confirmation) {
      newErrors.password_confirmation = 'As senhas não conferem';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);
    try {
      const cleanIdentifier = formData.identifier.replace(/\D/g, '');
      const isCpf = cleanIdentifier.length === 11 && !formData.identifier.includes('@');

      const response = await authService.setDependentPassword({
        ...(isCpf ? { cpf: formData.identifier } : { email: formData.identifier }),
        password: formData.password,
        password_confirmation: formData.password_confirmation,
      });

      completeAuth(response);
      navigate('/meus-parceiros');
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.errors?.password?.[0] ||
          'Erro ao definir a senha. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-green-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <button
          onClick={() => navigate('/associado/login')}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Voltar para o login</span>
        </button>

        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-gradient-to-r from-green-500 to-green-700 rounded-xl flex items-center justify-center mb-4 shadow-lg">
              <KeyRound className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">Criar Senha de Acesso</h2>
            <p className="mt-2 text-sm text-gray-600">
              Este é o seu primeiro acesso. Informe seu e-mail ou CPF cadastrado e crie uma senha.
            </p>
          </div>

          {error && (
            <Alert variant="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <Input
              label="Email ou CPF"
              type="text"
              name="identifier"
              value={formData.identifier}
              onChange={handleChange}
              error={errors.identifier}
              placeholder="seu@email.com ou CPF"
              disabled={loading}
              autoFocus={!prefilledIdentifier}
            />

            <Input
              label="Nova Senha"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              placeholder="Mínimo 6 caracteres"
              disabled={loading}
              autoFocus={!!prefilledIdentifier}
            />

            <Input
              label="Confirmar Senha"
              type="password"
              name="password_confirmation"
              value={formData.password_confirmation}
              onChange={handleChange}
              error={errors.password_confirmation}
              disabled={loading}
            />

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800"
              loading={loading}
              disabled={loading}
            >
              {loading ? 'Criando senha...' : 'Criar Senha e Entrar'}
            </Button>
          </form>
        </div>

        <p className="mt-4 text-center text-xs text-gray-500">Cartão HMG - Seu parceiro em benefícios - 2025</p>
      </div>
    </div>
  );
};

export default DependentSetPassword;
